import React, { useRef, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

/**
 * A specialized audio player component that ensures compatibility with iOS devices.
 * Addresses common iOS audio playback issues:
 * 1. Requires user interaction before AudioContext initialization
 * 2. Handles autoplay restrictions
 * 3. Uses playsInline attribute to prevent fullscreen playback
 * 4. Implements audio unlocking via touchstart event
 * 
 * @param {Object} props Component props
 * @param {Function} props.onAudioUnlocked Callback when audio is successfully unlocked
 * @param {boolean} props.showControls Whether to show the audio controls
 * @param {string} props.buttonText Text to display on the unlock button
 * @param {boolean} props.triggerUnlock Whether to trigger audio unlocking
 */
const IosAudioPlayer = ({ 
  onAudioUnlocked, 
  showControls = false,
  buttonText = "Enable Audio Playback",
  triggerUnlock = false
}) => {
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // Detect iOS devices on component mount
  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Create a silent audio element for iOS
    if (iOS && audioRef.current) {
      // Set up audio element with attributes needed for iOS
      audioRef.current.setAttribute('playsinline', '');
      audioRef.current.setAttribute('webkit-playsinline', '');
      audioRef.current.preload = 'auto';
      
      // Create a short silent audio buffer
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // If AudioContext is suspended (common on iOS), we'll need user interaction
      if (audioContext.state === 'suspended') {
        console.log('AudioContext is suspended, will require user interaction');
      }
      
      // Try to unlock audio on first render - this might work in some cases
      setTimeout(() => {
        unlockAudio();
      }, 500);
    }
    
    return () => {
      // Clean up audio context on unmount
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(err => console.error('Error closing AudioContext:', err));
      }
    };
  }, []);

  // Function to unlock audio on iOS
  const unlockAudio = async () => {
    try {
      if (!audioRef.current || !audioContextRef.current) return;
      
      console.log('Attempting to unlock audio...');
      
      // Resume AudioContext (required for iOS)
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
        console.log('AudioContext resumed successfully');
      }
      
      // Play and immediately pause to unlock audio element
      // This is a common technique for iOS audio unlocking
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Audio playback started successfully
            audioRef.current.pause();
            console.log('Audio unlocked successfully');
            setAudioUnlocked(true);
            
            // Notify parent component that audio is unlocked
            if (onAudioUnlocked) {
              onAudioUnlocked(audioContextRef.current);
            }
          })
          .catch(error => {
            console.error('Audio unlock failed:', error);
            // Don't set error state here, as we'll retry with button click
          });
      }
    } catch (error) {
      console.error('Error unlocking audio:', error);
    }
  };

  // Watch for triggerUnlock prop changes to attempt unlocking
  useEffect(() => {
    if (triggerUnlock && isIOS && !audioUnlocked) {
      unlockAudio();
    }
  }, [triggerUnlock, isIOS, audioUnlocked]);

  // Add touchstart event listener for iOS devices
  useEffect(() => {
    const handleTouch = () => {
      if (!audioUnlocked) {
        unlockAudio();
      }
    };

    if (isIOS) {
      // Add listeners to multiple events to increase chances of unlocking
      document.addEventListener('touchstart', handleTouch, { once: true });
      document.addEventListener('touchend', handleTouch, { once: true });
      document.addEventListener('click', handleTouch, { once: true });
    }

    return () => {
      if (isIOS) {
        document.removeEventListener('touchstart', handleTouch);
        document.removeEventListener('touchend', handleTouch);
        document.removeEventListener('click', handleTouch);
      }
    };
  }, [audioUnlocked, isIOS]);

  // If not iOS or audio already unlocked, and we don't want to show controls, render nothing
  if ((!isIOS || audioUnlocked) && !showControls) {
    return null;
  }

  // If iOS and not unlocked but we don't want to show controls, render a hidden audio element
  if (isIOS && !audioUnlocked && !showControls) {
    return (
      <audio 
        ref={audioRef}
        playsInline 
        preload="auto"
        src="data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV"
        style={{ display: 'none' }}
      />
    );
  }

  return (
    <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Hidden audio element */}
      <audio 
        ref={audioRef}
        playsInline 
        preload="auto"
        src="data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV"
        style={{ display: 'none' }}
      />
      
      {/* Only show unlock button if on iOS and audio not yet unlocked */}
      {isIOS && !audioUnlocked && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            iOS requires user interaction to enable audio
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={unlockAudio}
          >
            {buttonText}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default IosAudioPlayer;
